<template>
  <form @submit="onSubmit">
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="keyAttribute"
        :label="t('productTransformationLookupLink.detail.keyAttribute')"
        :placeholder="$t('productTransformationLookupLink.detail.keyAttribute')"
        :options="keyAttributeOptions"
        :custom-label="attributeLabel"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="valueAttribute"
        :label="t('productTransformationLookupLink.detail.valueAttribute')"
        :placeholder="$t('productTransformationLookupLink.detail.valueAttribute')"
        :options="valueAttributeOptions"
        :custom-label="attributeLabel"
      />
    </vl-form-column>
    <vl-action-group v-vl-spacer:bottom.small>
      <vl-button icon="save" :mod-loading="store.reloading" mod-icon-before type="submit">
        {{ $t('general.save') }}
      </vl-button>
      <vl-button icon="cross" type="button" mod-icon-before mod-secondary mod-error @click="editStore.closeCreate">
        {{ $t('general.cancel') }}
      </vl-button>
    </vl-action-group>
  </form>
</template>

<script lang="ts" setup>
import { computed, onMounted, watch } from 'vue';
import { EntityAttributeDetailDto, SourceEntityDetailDto } from '@/api/backoffice/clients';
import { useForm } from 'vee-validate';
import { object } from 'yup';
import { useI18n } from 'vue-i18n';
import { useProductProcessDefinitionEditStore } from '@/modules/configuration/store/product-process-definition/product-process-definition-edit.store';
import { useProductTransformationLookupLinkStore } from '@/modules/configuration/store/product-transformation-lookup-link/product-transformation-lookup-link.store';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';

const { t } = useI18n();

const store = useProductTransformationLookupLinkStore();
const editStore = useProductProcessDefinitionEditStore();

const props = defineProps<{
  sourceEntity: SourceEntityDetailDto;
}>();

const validationSchema = object({
  keyAttribute: object().required(
    t('validations.messages.required', { _field_: t('productTransformationLookupLink.detail.keyAttribute') })
  ),
  valueAttribute: object().required(
    t('validations.messages.required', { _field_: t('productTransformationLookupLink.detail.valueAttribute') })
  )
});

const form = useForm();

const { resetForm, handleSubmit, values, setFieldValue } = useForm({
  validationSchema
});

const keyAttributeOptions = computed(() => {
  return props.sourceEntity?.model?.attributes.filter((x) => x.id !== values.valueAttribute?.id) ?? [];
});

const valueAttributeOptions = computed(() => {
  return props.sourceEntity?.model?.attributes.filter((x) => x.id !== values.keyAttribute?.id) ?? [];
});

watch(
  keyAttributeOptions,
  () => {
    if (!keyAttributeOptions.value.find((x) => x.id === values?.keyAttribute?.id)) {
      setFieldValue('keyAttribute', null, false);
    }
  },
  { deep: true }
);

watch(
  valueAttributeOptions,
  () => {
    if (!valueAttributeOptions.value.find((x) => x.id === values?.valueAttribute?.id)) {
      setFieldValue('valueAttribute', null, false);
    }
  },
  { deep: true }
);

onMounted(async () => {
  resetForm();
});

const onSuccess = async (values) => {
  await store.create(props.sourceEntity.id, values.keyAttribute?.id, values.valueAttribute?.id);
  editStore.closeCreate();
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const attributeLabel = (type: EntityAttributeDetailDto) => {
  return `${type.code} (${type.label})`;
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);

defineExpose({ onSubmit });
</script>
